import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Users, CheckCircle2, UserX, Activity, Monitor, ScanLine, LayoutDashboard, Layers, DoorOpen, UserCog } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import { AppHeader } from "../components/AppHeader";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { useTenantSocket } from "../lib/ws";
import { ServicesPanel, CountersPanel, UsersPanel } from "./AdminPanels";

const COLORS = ["#2563EB", "#DC2626", "#7C3AED", "#16A34A", "#D97706", "#0891B2"];

const TABS = [
  { key: "overview", icon: LayoutDashboard, label: "overview" },
  { key: "services", icon: Layers, label: "services" },
  { key: "counters", icon: DoorOpen, label: "counters" },
  { key: "users", icon: UserCog, label: "manage_users" },
];

export default function Admin() {
  const { tenant, user } = useAuth();
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("overview");

  const load = async () => {
    const { data } = await api.get("/dashboard/stats");
    setStats(data);
  };

  useEffect(() => { if (tab === "overview") load(); }, [tab]);
  useTenantSocket(tenant?.slug, () => { if (tab === "overview") load(); });

  const totals = stats?.totals || { waiting: 0, called: 0, done: 0, absent: 0, total: 0 };
  const isDemo = !!user?.is_demo;

  return (
    <div className="min-h-screen">
      <AppHeader activeItem="admin" />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="text-sm uppercase tracking-[0.25em] text-blue-700 font-medium">{t("dashboard")}</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">{tenant?.name}</h1>
          </div>
          <div className="flex items-center gap-2">
            <Link to={`/kiosk/${tenant?.slug}`} target="_blank" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-sm">
              <ScanLine className="w-4 h-4" /> {t("open_kiosk")}
            </Link>
            <Link to={`/display/${tenant?.slug}`} target="_blank" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-sm">
              <Monitor className="w-4 h-4" /> {t("open_display")}
            </Link>
            <Link to="/staff" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-700 text-white hover:bg-blue-800 text-sm">
              <Users className="w-4 h-4" /> {t("staff")}
            </Link>
          </div>
        </div>

        <div className="flex flex-wrap gap-1 mb-6 bg-white border border-slate-200 rounded-full p-1 w-fit" data-testid="admin-tabs">
          {TABS.map((tt) => {
            const Icon = tt.icon;
            const active = tab === tt.key;
            return (
              <button
                key={tt.key}
                onClick={() => setTab(tt.key)}
                data-testid={`admin-tab-${tt.key}`}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm transition ${active ? "bg-blue-700 text-white" : "text-slate-600 hover:text-slate-900"}`}
              >
                <Icon className="w-4 h-4" />
                {t(tt.label)}
              </button>
            );
          })}
        </div>

        {tab === "overview" && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
              <StatCard label={t("total_today")} value={totals.total} icon={Activity} tone="bg-slate-50 text-slate-900 border-slate-200" />
              <StatCard label={t("waiting")} value={totals.waiting} icon={Clock} tone="bg-blue-50 text-blue-900 border-blue-100" />
              <StatCard label={t("called")} value={totals.called} icon={Users} tone="bg-violet-50 text-violet-900 border-violet-100" />
              <StatCard label={t("done")} value={totals.done} icon={CheckCircle2} tone="bg-emerald-50 text-emerald-900 border-emerald-100" />
              <StatCard label={t("absent")} value={totals.absent} icon={UserX} tone="bg-rose-50 text-rose-900 border-rose-100" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-5">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading font-semibold text-lg">{t("hourly_traffic")}</h3>
                  <div className="text-sm text-slate-500">{t("avg_wait_time")}: <span className="font-semibold text-slate-900">{stats?.avg_wait_min ?? 0} {t("minutes")}</span></div>
                </div>
                <div className="mt-4 h-64">
                  <ResponsiveContainer>
                    <LineChart data={stats?.hourly || []} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid stroke="#F1F5F9" />
                      <XAxis dataKey="hour" tick={{ fontSize: 12, fill: "#64748B" }} />
                      <YAxis tick={{ fontSize: 12, fill: "#64748B" }} allowDecimals={false} />
                      <Tooltip />
                      <Line type="monotone" dataKey="count" stroke="#1D4ED8" strokeWidth={2.5} dot={{ r: 3, fill: "#1D4ED8" }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 p-5">
                <h3 className="font-heading font-semibold text-lg">{t("patients_by_service")}</h3>
                <div className="mt-4 h-64">
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={stats?.by_service || []} dataKey="count" nameKey="service" outerRadius={80} innerRadius={44}>
                        {(stats?.by_service || []).map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="lg:col-span-3 bg-white rounded-2xl border border-slate-200 p-5">
                <h3 className="font-heading font-semibold text-lg">{t("staff_performance")}</h3>
                <div className="mt-4 h-64">
                  <ResponsiveContainer>
                    <BarChart data={stats?.staff || []} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid stroke="#F1F5F9" />
                      <XAxis dataKey="agent" tick={{ fontSize: 11, fill: "#64748B" }} interval={0} />
                      <YAxis tick={{ fontSize: 12, fill: "#64748B" }} allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="done" fill="#1D4ED8" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </>
        )}

        {tab === "services" && <ServicesPanel isDemo={isDemo} />}
        {tab === "counters" && <CountersPanel isDemo={isDemo} />}
        {tab === "users" && <UsersPanel isDemo={isDemo} currentUserId={user?.id} />}
      </main>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tone }) {
  return (
    <div className={`rounded-2xl border p-4 ${tone}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.2em] opacity-80">{label}</div>
        <Icon className="w-4 h-4 opacity-70" />
      </div>
      <div className="font-heading text-3xl font-bold mt-2">{value}</div>
    </div>
  );
}
